import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { BehaviorSubject, isObservable, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { generateID } from '../../helpers/utils';
import {
  ScSelectionType,
  ScTableActionSelection,
  ScBaseTableViewConfig,
  ScCrudRules,
  ScTableAction,
  ScFormField,
  ScFieldConfig,
  ScRowAction,
  ScRowUpdate,
  ScEndpointFilter,
  ScFormFieldsFunction
} from '../../models/table.model';
import { ResponseError } from '../../services/rest-service.service';
import { ScDialogService } from '../sc-dialogs';
import { ScGenericFormComponent } from '../sc-generic-form/sc-generic-form.component';
import { ScPopoverService } from '../sc-popover';
import { ScPopoverRef } from './../sc-popover/sc-popover-ref';
import {
  ScAdvancedFiltersComponent,
  AdvancedFilterData
} from './components/sc-table-view-filters/sc-advanced-filters/sc-advanced-filters.component';
import { ScCrudService } from './sc-crud.service';
import { AddEntity, DeleteEntity, InitView, LoadData, SetData, UpdateEntity } from './state/view.actions';
import { ViewState } from './state/view.state';

@Component({
  selector: 'sc-table-view',
  templateUrl: './sc-table-view.component.html',
  styleUrls: ['./sc-table-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScTableViewComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('advancedFiltersTarget', { static: false }) advancedFiltersTarget: ElementRef;
  @Input() allowSelection: boolean;
  @Input() selectionType: ScSelectionType;
  @Input() allowAdd: boolean;
  @Input() filters: any;
  @Input() headerHeight: number;
  @Input() hasFilters: boolean;

  @Output() actionSelection: EventEmitter<ScTableActionSelection> = new EventEmitter();

  public data: any[];
  public selectedViewType: string;
  public formValue: any;
  public datasource: MatTableDataSource<any>;
  public paginatorData: any;
  public sortData: any;
  public selection = new SelectionModel<any>(true, []);
  public configSubject$: BehaviorSubject<ScBaseTableViewConfig> = new BehaviorSubject(null);
  public rowsSubject$: BehaviorSubject<any[]> = new BehaviorSubject(null);
  public toggleFilters: boolean;
  public instanceId: string;
  public totalAdvancedFilters: number;
  private _selected: any[] = [];
  private _viewConfig: ScBaseTableViewConfig;
  private _rules: ScCrudRules = new ScCrudRules();

  private _unsubscribe$: Subject<void> = new Subject();
  private _loading: boolean;
  private _advancedFiltersRef: ScPopoverRef;

  constructor(
    private _scPopoverService: ScPopoverService,
    private _store: Store,
    private _actions: Actions,
    private _cd: ChangeDetectorRef,
    private _crudService: ScCrudService,
    private _dialogService: ScDialogService,
    @Inject('env') private _env: any
  ) {}

  @Input() set viewConfig(viewConfig: ScBaseTableViewConfig) {
    if (viewConfig) {
      this._viewConfig = viewConfig;
      this.configSubject$.next(viewConfig);
    }
  }

  get viewConfig() {
    return this._viewConfig;
  }

  @Input() set rules(rules: ScCrudRules) {
    if (rules) {
      this._rules = new ScCrudRules(rules);
    }
  }

  get rules(): ScCrudRules {
    return this._rules;
  }

  @Input() set rows(rows: any[]) {
    if (rows) {
      this.rowsSubject$.next(rows);
    }
  }

  @Input() set rowToUpdate(rowToUpdate: ScRowUpdate) {
    if (rowToUpdate) {
      switch (rowToUpdate.type) {
        case ScRowAction.Add:
          this._store.dispatch(new AddEntity(this.instanceId, rowToUpdate.row));
          break;
        case ScRowAction.Update:
          this._store.dispatch(new UpdateEntity(this.instanceId, rowToUpdate.row));
          break;
        case ScRowAction.Delete:
          this._store.dispatch(new DeleteEntity(this.instanceId, rowToUpdate.row));
          break;
      }
    }
  }

  @Input() set selected(selected: any[]) {
    if (selected) {
      this._selected = selected;
      this.selected.forEach(row => this.selection.select(row));
    }
  }

  get selected() {
    return this._selected;
  }

  @Input() set loading(loading: boolean) {
    this._loading = loading;
  }

  get loading() {
    return this._loading;
  }

  public ngOnInit(): void {
    ScCrudService.subscription = this._unsubscribe$;

    this._store
      .select(ViewState.getData)
      .pipe(
        takeUntil(this._unsubscribe$),
        map(filterFn => filterFn(this.instanceId))
      )
      .subscribe(data => {
        this.data = data;
        this._cd.markForCheck();
      });

    this._store
      .select(ViewState.totalAdvancedFilters)
      .pipe(
        takeUntil(this._unsubscribe$),
        map(filterFn => filterFn(this.instanceId))
      )
      .subscribe(totalAdvancedFilters => {
        this.totalAdvancedFilters = totalAdvancedFilters;
        this._cd.markForCheck();
      });

    this._store
      .select(ViewState.filtersFormValue)
      .pipe(
        takeUntil(this._unsubscribe$),
        map(filterFn => filterFn(this.instanceId)),
        filter(v => !!v)
      )
      .subscribe(formValue => {
        this.formValue = formValue;
        this._cd.markForCheck();
      });

    this._store
      .select(ViewState.loading)
      .pipe(
        takeUntil(this._unsubscribe$),
        map(filterFn => filterFn(this.instanceId))
      )
      .subscribe(loading => {
        this._loading = loading;
        this._cd.markForCheck();
      });

    this._actions
      .pipe(
        takeUntil(this._unsubscribe$),
        ofActionDispatched(InitView),
        switchMap(() => this.rowsSubject$),
        filter(rows => !!rows && !this.viewConfig.crud)
      )
      .subscribe((data: any[]) => {
        this._store.dispatch(new SetData(this.instanceId, data));
      });
  }

  public fileViewer(row) {
    const url = `${this._env.apiURL}/Files/GetFile/${row.docLink}`;
    this._dialogService.fileViewer(row.filename || row.fileName, row.docType, url);
  }

  public ngAfterViewInit(): void {
    this.configSubject$.pipe(takeUntil(this._unsubscribe$)).subscribe(config => {
      this.instanceId = config.title ? config.title : `${generateID()}`;
      this.datasource = new MatTableDataSource();
      // this.datasource.paginator = this.paginator;
      this._store.dispatch(new InitView(this.instanceId, config, this.datasource));
    });

    this._store
      .select(ViewState.paginatorData)
      .pipe(
        takeUntil(this._unsubscribe$),
        map(filterFn => filterFn(this.instanceId)),
        filter(v => !!v)
      )
      .subscribe(paginatorData => {
        this.paginatorData = paginatorData;
        this._cd.markForCheck();
      });

    this._store
      .select(ViewState.sortData)
      .pipe(
        takeUntil(this._unsubscribe$),
        map(filterFn => filterFn(this.instanceId)),
        filter(v => !!v)
      )
      .subscribe(sortData => {
        this.sortData = sortData;
        this._cd.markForCheck();
      });

    this.paginator.page.pipe(takeUntil(this._unsubscribe$)).subscribe(() => {
      const filters = this._store.selectSnapshot(ViewState.filters)(this.instanceId);
      const advancedFilters = this._store.selectSnapshot(ViewState.advancedFilters)(this.instanceId);
      const sortData = this._store.selectSnapshot(ViewState.sortData)(this.instanceId);
      this._store.dispatch(
        new LoadData(
          this.instanceId,
          this.paginator.pageIndex,
          this.paginator.pageSize,
          filters,
          advancedFilters,
          sortData.activeSort,
          sortData.direction
        )
      );
    });
  }

  public sortChange({ active, direction }) {
    const filters = this._store.selectSnapshot(ViewState.filters)(this.instanceId);
    const advancedFilters = this._store.selectSnapshot(ViewState.advancedFilters)(this.instanceId);
    this._store.dispatch(
      new LoadData(this.instanceId, this.paginator.pageIndex, this.paginator.pageSize, filters, advancedFilters, active, direction)
    );
  }

  public filter(filters: ScEndpointFilter[]) {
    const advancedFilters = this._store.selectSnapshot(ViewState.advancedFilters)(this.instanceId);
    this._store.dispatch(new LoadData(this.instanceId, 0, this.paginator.pageSize, filters, advancedFilters));
  }

  public openAdvancedFilters() {
    if (this._advancedFiltersRef) {
      return;
    }
    const filters = this._store.selectSnapshot(ViewState.filters)(this.instanceId);
    const advancedFilters = this._store.selectSnapshot(ViewState.advancedFilters)(this.instanceId);
    const notAllowedColumns = ['link', 'action', 'routelink', 'externallink', 'button', 'task', 'icon'];
    const filterablecolumns = this.viewConfig.columns
      .filter(c => !notAllowedColumns.some(n => n === c.type))
      .filter(c => typeof c.prop === 'string');

    this._advancedFiltersRef = this._scPopoverService.open<AdvancedFilterData>(
      ScAdvancedFiltersComponent,
      this.advancedFiltersTarget.nativeElement,
      {
        hasBackDrop: false,
        data: {
          columns: filterablecolumns.map(c => c.prop) || [],
          initialFilters: [...advancedFilters]
        },
        disableClose: false
      }
    );

    this._advancedFiltersRef
      .afterClosed()
      .pipe(
        take(1),
        tap(() => (this._advancedFiltersRef = null)),
        filter(f => !!f)
      )
      .subscribe((data: ScEndpointFilter[]) => {
        this._store.dispatch(new LoadData(this.instanceId, 0, this.paginator.pageSize, filters, data));
      });
  }

  public action(action: ScTableActionSelection) {
    switch (action.type) {
      case ScTableAction.Edit:
        this.openDialog(action.data);
        break;
      case ScTableAction.Duplicate:
        this.openDialog(action.data, true);
        break;
      case ScTableAction.Delete:
        this.deleteItem(action.data);
        break;
      default:
        this.actionSelection.emit(action);
    }
  }

  public add() {
    this.openDialog();
  }

  public deleteItem(data: any) {
    const crud = this._store.selectSnapshot(ViewState.crudConfig)(this.instanceId);
    if (!crud) {
      return this.actionSelection.emit({
        type: ScTableAction.Delete,
        data
      });
    }
    const { itemId, remove } = crud;
    this._dialogService
      .confirm('Are you sure you want to delete this item?')
      .pipe(
        take(1),
        filter(confirmed => confirmed),
        switchMap(() =>
          this._crudService.delete({
            id: data.id,
            config: {
              ...remove,
              itemId
            }
          })
        )
      )
      .subscribe(() => {
        this._store.dispatch(new DeleteEntity(this.instanceId, data));
      });
  }

  public openDialog(data?: any, isForDuplicate?: boolean): void {
    const crud = this._store.selectSnapshot(ViewState.crudConfig)(this.instanceId);
    if (!crud) {
      return this.actionSelection.emit({
        type: ScTableAction.Create,
        data
      });
    }

    const { itemId, update, get, create, formFields, isForFormly, formComponent } = crud;

    this._getFormFields(formFields)
      .pipe(
        switchMap(fields =>
          this._dialogService
            .generic({
              componentRef: formComponent || ScGenericFormComponent,
              formFields: fields,
              title: data && data.id ? 'Edit' : 'Create',
              data: { ...data } || ({} as any),
              isForFormly
            })
            .pipe(
              take(1),
              filter(item => !!item),
              switchMap((item: any) => {
                if (isForDuplicate) {
                  delete item[itemId];
                  delete item.id;
                }

                if (!item.id) {
                  return this._crudService
                    .create({
                      data: item,
                      config: create,
                      getConfig: {
                        ...get,
                        itemId
                      },
                      itemId
                    })
                    .pipe(
                      tap((addedItem: any) => {
                        this._store.dispatch(new AddEntity(this.instanceId, addedItem));
                      })
                    );
                }
                return this._crudService
                  .update({
                    data: item,
                    config: {
                      ...update,
                      itemId
                    },
                    getConfig: {
                      ...get,
                      itemId
                    },
                    itemId
                  })
                  .pipe(
                    tap((updatedItem: any) => {
                      this._store.dispatch(new UpdateEntity(this.instanceId, updatedItem));
                    })
                  );
              })
            )
        )
      )
      .subscribe(
        (item: any) => {},
        (error: ResponseError) => {
          if (error && error.data) {
            this.openDialog(error.data);
          }
        }
      );
  }

  public ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    if (this._advancedFiltersRef) {
      this._advancedFiltersRef.close();
    }
  }

  private _getFormFields(formFields: ScFormField[] | ScFieldConfig[] | ScFormFieldsFunction): Observable<ScFormField[] | ScFieldConfig[]> {
    if (Array.isArray(formFields)) {
      return of(formFields);
    } else if (isObservable(formFields)) {
      return formFields as Observable<ScFormField[] | ScFieldConfig[]>;
    } else {
      return this._getFormFields(formFields.call(this._crudService.scope));
    }
  }
}
